import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useGetOnDemandStaffByManagementCompanyQuery } from '../../../redux/managementCompanyWorkforce';
import {
	viewStateForWorkforceShiftMed,
	updateShiftMedViewStateForManagementCompany
} from '../../../redux/flexWorkforceView';
import { PRIVATE_PATH } from '../../../constants';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import personIcon from '../../../assets/images/person-icon.svg';

import '../../../components/professionals/ProfessionalsList.scss';
import './ShiftMedOnDemandStaffList.scss';

// Column widths are shared between the main table and the expansion sub-table (both use a fixed table
// layout) so the sub-table's "License type"/"Specialty" columns line up under the matching headers.
const COLUMN_WIDTHS = {
	expander: '4%',
	professional: '18%',
	phone: '12%',
	licenseType: '12%',
	specialty: '22%',
	status: '11%',
	credentialsVisible: '13%',
	actions: '8%'
};

// Leading spacer in the sub-table spans the expander + Professional + Phone columns.
const EXPANSION_LEADING_WIDTH = '34%';

// Trailing spacer in the sub-table spans the Status + Credentials visible + Actions columns.
const EXPANSION_TRAILING_WIDTH = '32%';

const STATUS_LABELS = { WORKED: 'Worked', NOT_WORKED: 'Not Worked' };

const CREDENTIALS_VISIBLE_LABEL = 'View';

// Default sort matches the Internal/Locations tabs: ascending by the first (name) column.
const DEFAULT_SORT = { field: 'name', order: 1 };

// The expansion sub-table's columns are marked sortable only so the header shows the standard sort caret;
// this no-op keeps that table controlled so clicking never reorders its rows. (Main-table sorting is real.)
const noopSort = () => {};

// Order licenses by resource group (professional_license_groups.name) alphabetically, then by license rank
// descending within a group (RN before LPN before CNA). Single group → pure rank order (RN, LPN, CNA);
// multiple groups → group-alphabetical then rank (e.g. DH, DA, RN, LPN). Mirrors the Internal Flex rank
// ordering and extends it across resource groups.
const compareLicenses = (a, b) => {
	const groupCompare = (a.licenseGroupName || '').localeCompare(b.licenseGroupName || '');
	if (groupCompare !== 0) return groupCompare;
	return (b.licenseRank || 0) - (a.licenseRank || 0);
};

// Distinct licenses from the professional's shift types, ordered by (resource group, rank).
const orderedDistinctLicenses = (shiftTypes) => {
	const byCode = new Map();
	shiftTypes.forEach((shiftType) => {
		if (!byCode.has(shiftType.licenseShortCode)) {
			byCode.set(shiftType.licenseShortCode, {
				code: shiftType.licenseShortCode,
				licenseRank: shiftType.licenseRank,
				licenseGroupName: shiftType.licenseGroupName
			});
		}
	});
	return [...byCode.values()].sort(compareLicenses);
};

// License type: distinct licenses ordered by resource group then rank (e.g. "DH, DA, RN, LPN").
const formatLicenseType = (shiftTypes) =>
	orderedDistinctLicenses(shiftTypes)
		.map((license) => license.code)
		.join(', ');

// Specialty: specialties grouped under their parent license; licenses ordered by resource group then rank,
// and the specialties within a license ordered alphabetically (e.g. "RN - ED/ER, Med/Surg, Tele; RT - Peds").
// Shift types that are a bare license (no specialty) contribute only to the License type column.
const formatSpecialty = (shiftTypes) => {
	const byLicense = new Map();
	shiftTypes.forEach((shiftType) => {
		if (!shiftType.specialtyShortName) return;
		if (!byLicense.has(shiftType.licenseShortCode)) {
			byLicense.set(shiftType.licenseShortCode, {
				code: shiftType.licenseShortCode,
				licenseRank: shiftType.licenseRank,
				licenseGroupName: shiftType.licenseGroupName,
				specialties: new Set()
			});
		}
		byLicense.get(shiftType.licenseShortCode).specialties.add(shiftType.specialtyShortName);
	});
	const groups = [...byLicense.values()].sort(compareLicenses).map((license) => {
		const specialties = [...license.specialties].sort((a, b) => a.localeCompare(b));
		return `${license.code} - ${specialties.join(', ')}`;
	});
	return groups.length ? groups.join('; ') : '-';
};

// Order the expanded sub-table's rows the same way as the collapsed columns: by license (resource group,
// then rank), and within a license the bare-license row (no specialty) first, then specialties alphabetically.
const compareShiftTypeRows = (a, b) => {
	const licenseCompare = compareLicenses(a, b);
	if (licenseCompare !== 0) return licenseCompare;
	return (a.specialtyShortName || '').localeCompare(b.specialtyShortName || '');
};

const mapOnDemandStaff = (data) =>
	data?.map((professional) => ({
		id: professional.caregiverId,
		name: professional.name || '-',
		phone: professional.phone || '-',
		licenseType: formatLicenseType(professional.shiftTypes),
		specialty: formatSpecialty(professional.shiftTypes),
		// Plain-text values so sorting and the search bar work on them; the templates below draw the pill and link.
		status: professional.hasWorked ? STATUS_LABELS.WORKED : STATUS_LABELS.NOT_WORKED,
		credentialsVisible: professional.isOnDemandStaff ? CREDENTIALS_VISIBLE_LABEL : '-',
		isOnDemandStaff: professional.isOnDemandStaff,
		hasWorked: professional.hasWorked,
		shiftTypes: professional.shiftTypes,
		expandable: professional.shiftTypes.length > 1
	})) || [];

const specialtyCombosTemplate = (shiftType) => shiftType.specialtyShortName || '-';

const statusTemplate = (rowData) => (
	<span className={`worked-status-pill ${rowData.status === STATUS_LABELS.WORKED ? 'worked' : 'not-worked'}`}>
		{rowData.status}
	</span>
);

// "View" opens the professional's credentials page; it shows only for professionals on the On Demand Staff list,
// since the credentials page reads those associations.
const CredentialsVisibleCell = memo(({ rowData, onView }) => {
	const handleClick = useCallback(() => onView(rowData), [onView, rowData]);

	if (!rowData.isOnDemandStaff) {
		return rowData.credentialsVisible;
	}

	return (
		<button type="button" className="btn btn-link credentials-view-link" onClick={handleClick}>
			{rowData.credentialsVisible}
		</button>
	);
});

const ShiftMedOnDemandStaffList = ({ managementCompany, filters }) => {
	const [expandedRows, setExpandedRows] = useState(null);
	const [filteredCount, setFilteredCount] = useState(null);

	const { data, isLoading } = useGetOnDemandStaffByManagementCompanyQuery(managementCompany.id, {
		refetchOnMountOrArgChange: true
	});

	const professionals = useMemo(() => mapOnDemandStaff(data), [data]);

	const dispatch = useDispatch();
	const history = useHistory();
	const viewState = useSelector(viewStateForWorkforceShiftMed(managementCompany.id));
	const sort = viewState?.sort || DEFAULT_SORT;

	// Open the read-only ShiftMed profile: the credentials page for On Demand Staff, otherwise the Shift History page
	// (a professional who only worked here has no credentials page). The name, On Demand Staff flag and worked flag
	// travel via router state so the standalone page can title itself and build its sidebar without an extra fetch.
	const goToProfessional = useCallback(
		(rowData) =>
			history.push(
				`${PRIVATE_PATH.WORKFORCE_PAGE}/${managementCompany.id}/on-demand-staff/${rowData.id}/${
					rowData.isOnDemandStaff ? 'credentials' : 'shift-history'
				}`,
				{
					professionalName: rowData.name,
					isOnDemandStaff: rowData.isOnDemandStaff,
					hasWorked: rowData.hasWorked
				}
			),
		[history, managementCompany.id]
	);

	const onSort = (e) => {
		dispatch(
			updateShiftMedViewStateForManagementCompany({
				managementCompanyId: managementCompany.id,
				sort: { field: e.sortField, order: e.sortOrder }
			})
		);
	};

	useEffect(() => {
		setFilteredCount(null);
	}, [managementCompany.id]);

	const onFilter = (e) => setFilteredCount(e.length);
	const count = filteredCount !== null ? filteredCount : professionals.length;

	const allowExpansion = useCallback((rowData) => rowData.expandable, []);

	const cellClass = useCallback(() => ({ 'table-cell': true }), []);

	const rowClass = useCallback(
		(rowData) => ({
			'expanded-bundle-row': !!expandedRows && Object.prototype.hasOwnProperty.call(expandedRows, rowData.id)
		}),
		[expandedRows]
	);

	const actionsTemplate = useCallback(
		(rowData) => (
			<div className="actions-container">
				<OverlayTrigger
					placement="bottom"
					overlay={
						<Tooltip className="workforce-action-tooltip" id={`manage-profile-${rowData.id}`}>
							View profile
						</Tooltip>
					}
				>
					<button
						type="button"
						className="btn action-menu"
						aria-label="View profile"
						onClick={() => goToProfessional(rowData)}
					>
						<img src={personIcon} alt="View profile" />
					</button>
				</OverlayTrigger>
			</div>
		),
		[goToProfessional]
	);

	const credentialsVisibleTemplate = useCallback(
		(rowData) => <CredentialsVisibleCell rowData={rowData} onView={goToProfessional} />,
		[goToProfessional]
	);

	const rowExpansionTemplate = useCallback(
		(rowData) => (
			<div className="shiftmed-combos">
				<DataTable
					value={[...rowData.shiftTypes].sort(compareShiftTypeRows)}
					size="small"
					tableStyle={{ tableLayout: 'fixed' }}
					onSort={noopSort}
				>
					<Column style={{ width: EXPANSION_LEADING_WIDTH }} />
					<Column
						field="licenseShortCode"
						header="License type"
						style={{ width: COLUMN_WIDTHS.licenseType }}
						sortable
					/>
					<Column
						header="Specialty"
						style={{ width: COLUMN_WIDTHS.specialty }}
						body={specialtyCombosTemplate}
						sortable
					/>
					<Column style={{ width: EXPANSION_TRAILING_WIDTH }} />
				</DataTable>
			</div>
		),
		[]
	);

	return (
		<div className="professionals-list shiftmed-on-demand-staff-list">
			<div className="toolbar">
				<p className="total-count">
					{count} Professional{count === 1 ? '' : 's'}
				</p>
			</div>
			<div className="datatable-wrapper">
				<div className="wrapper-container">
					<DataTable
						value={professionals}
						scrollable
						scrollHeight="flex"
						dataKey="id"
						tableStyle={{ minWidth: '60rem', tableLayout: 'fixed' }}
						size="small"
						stripedRows
						cellClassName={cellClass}
						rowClassName={rowClass}
						loading={isLoading}
						loadingIcon={<LoadingSpinner />}
						filters={filters}
						globalFilterFields={['name', 'phone', 'licenseType', 'specialty', 'status', 'credentialsVisible']}
						onValueChange={onFilter}
						emptyMessage={count ? '' : 'No professionals found. Clear or refine your search to see professionals.'}
						expandedRows={expandedRows}
						onRowToggle={(event) => setExpandedRows(event.data)}
						rowExpansionTemplate={rowExpansionTemplate}
						removableSort
						sortField={sort.field}
						sortOrder={sort.order}
						onSort={onSort}
					>
						<Column expander={allowExpansion} style={{ width: COLUMN_WIDTHS.expander }} />
						<Column field="name" header="Professional" style={{ width: COLUMN_WIDTHS.professional }} sortable />
						<Column field="phone" header="Phone" style={{ width: COLUMN_WIDTHS.phone }} sortable />
						<Column field="licenseType" header="License type" style={{ width: COLUMN_WIDTHS.licenseType }} sortable />
						<Column field="specialty" header="Specialty" style={{ width: COLUMN_WIDTHS.specialty }} sortable />
						<Column
							field="status"
							header="Status"
							style={{ width: COLUMN_WIDTHS.status }}
							body={statusTemplate}
							sortable
						/>
						<Column
							field="credentialsVisible"
							header="Credentials"
							style={{ width: COLUMN_WIDTHS.credentialsVisible }}
							body={credentialsVisibleTemplate}
							sortable
						/>
						<Column header="Actions" style={{ width: COLUMN_WIDTHS.actions }} body={actionsTemplate} />
					</DataTable>
				</div>
			</div>
		</div>
	);
};

export default memo(ShiftMedOnDemandStaffList);
